import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { ReaderBook } from "../lib/types.js";

type Theme = "light" | "dark" | "sepia";
const GAP = 48; // column gutter in px

interface Layout {
  pageW: number;
  pageH: number;
  viewportW: number;
  twoPage: boolean;
  totalViews: number;
  totalPages: number;
  pagesPerView: number;
  chapterViews: { title: string; view: number }[];
}

/**
 * The hosted web book reader. Renders the manuscript as a paginated book —
 * a two-page spread on desktop, one page on mobile — with a cover, page-turn
 * animation, chapter navigation, themes, fullscreen, and remembered position.
 * Pagination is produced dynamically with CSS multi-column layout.
 */
export function EbookReader({ projectId }: { projectId: string }) {
  const [book, setBook] = useState<ReaderBook | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [view, setView] = useState(0);
  const [layout, setLayout] = useState<Layout | null>(null);
  const [theme, setTheme] = useState<Theme>("light");
  const [fontScale, setFontScale] = useState(1);
  const [showToc, setShowToc] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const flowRef = useRef<HTMLDivElement | null>(null);
  const posKey = `clovascribe:read:${projectId}`;

  // Load the book + persisted preferences.
  useEffect(() => {
    document.title = "ClovaScribe · Reader";
    api
      .reader(projectId)
      .then((b) => setBook(b))
      .catch((e) => setError(e instanceof Error ? e.message : "Could not open this book."));
    try {
      const t = localStorage.getItem(`${posKey}:theme`) as Theme | null;
      if (t === "light" || t === "dark" || t === "sepia") setTheme(t);
      const fs = Number(localStorage.getItem(`${posKey}:font`));
      if (fs >= 0.8 && fs <= 1.6) setFontScale(fs);
    } catch {
      /* ignore */
    }
  }, [projectId, posKey]);

  // If there's no cover, open straight into the pages.
  useEffect(() => {
    if (book && !book.coverImage) setOpened(true);
  }, [book]);

  // (Re)compute pagination from the current container size + font scale.
  const measure = useCallback(() => {
    const vp = viewportRef.current;
    const flow = flowRef.current;
    if (!vp || !flow || !book) return;
    const host = vp.parentElement!;
    const availW = host.clientWidth;
    const availH = host.clientHeight;
    const twoPage = availW >= 820;
    const pageW = twoPage ? Math.min(Math.floor((availW - GAP) / 2) - 24, 560) : Math.min(availW - 32, 620);
    const pageH = availH;
    const pagesPerView = twoPage ? 2 : 1;
    const viewportW = twoPage ? pageW * 2 + GAP : pageW;

    // Apply sizing to the flow, then read how many columns the content produced.
    flow.style.height = `${pageH}px`;
    flow.style.width = `${viewportW}px`;
    flow.style.columnWidth = `${pageW}px`;
    flow.style.columnGap = `${GAP}px`;
    // Force reflow read.
    const totalPages = Math.max(1, Math.round((flow.scrollWidth + GAP) / (pageW + GAP)));
    const totalViews = Math.max(1, Math.ceil(totalPages / pagesPerView));

    const chapterViews: { title: string; view: number }[] = [];
    flow.querySelectorAll<HTMLElement>(".reader-chapter").forEach((el) => {
      const col = Math.round(el.offsetLeft / (pageW + GAP));
      chapterViews.push({ title: el.dataset.title ?? "Chapter", view: Math.floor(col / pagesPerView) });
    });

    setLayout({ pageW, pageH, viewportW, twoPage, totalViews, totalPages, pagesPerView, chapterViews });
    setView((v) => Math.min(v, totalViews - 1));
  }, [book]);

  useLayoutEffect(() => {
    if (!opened || !book) return;
    measure();
  }, [opened, book, fontScale, measure]);

  // Restore saved position once we know the layout.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!layout || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const saved = Number(localStorage.getItem(posKey));
      if (saved >= 0) setView(Math.min(saved, layout.totalViews - 1));
    } catch {
      /* ignore */
    }
  }, [layout, posKey]);

  useEffect(() => {
    if (!opened) return;
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [opened, measure]);

  // Persist position + preferences.
  useEffect(() => {
    try {
      localStorage.setItem(posKey, String(view));
    } catch {
      /* ignore */
    }
  }, [view, posKey]);
  useEffect(() => {
    try {
      localStorage.setItem(`${posKey}:theme`, theme);
      localStorage.setItem(`${posKey}:font`, String(fontScale));
    } catch {
      /* ignore */
    }
  }, [theme, fontScale, posKey]);

  const totalViews = layout?.totalViews ?? 1;
  const next = useCallback(() => setView((v) => Math.min(v + 1, totalViews - 1)), [totalViews]);
  const prev = useCallback(() => setView((v) => Math.max(v - 1, 0)), []);

  // Keyboard navigation.
  useEffect(() => {
    if (!opened) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "PageDown") next();
      else if (e.key === "ArrowLeft" || e.key === "PageUp") prev();
      else if (e.key === "Escape") setShowToc(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opened, next, prev]);

  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void rootRef.current?.requestFullscreen().catch(() => {});
  };

  if (error) {
    return <div className="reader-root" data-theme="light"><div className="reader-message">{error}</div></div>;
  }
  if (!book) {
    return <div className="reader-root" data-theme="light"><div className="reader-message">Opening book…</div></div>;
  }

  const offset = layout ? view * layout.pagesPerView * (layout.pageW + GAP) : 0;
  const firstFolio = layout ? view * layout.pagesPerView + 1 : 1;

  return (
    <div className="reader-root" data-theme={theme} ref={rootRef}>
      <header className="reader-bar">
        <div className="reader-bar-title truncate">{book.title}</div>
        <div className="reader-bar-tools">
          <button className="reader-tool" onClick={() => setShowToc((v) => !v)} title="Contents">☰ Contents</button>
          <div className="reader-themes">
            <button className={theme === "light" ? "on" : ""} onClick={() => setTheme("light")} title="Light">☀</button>
            <button className={theme === "sepia" ? "on" : ""} onClick={() => setTheme("sepia")} title="Sepia">◐</button>
            <button className={theme === "dark" ? "on" : ""} onClick={() => setTheme("dark")} title="Dark">☾</button>
          </div>
          <button className="reader-tool" onClick={() => setFontScale((f) => Math.max(0.8, +(f - 0.1).toFixed(2)))} title="Smaller text">A−</button>
          <button className="reader-tool" onClick={() => setFontScale((f) => Math.min(1.6, +(f + 0.1).toFixed(2)))} title="Larger text">A+</button>
          <button className="reader-tool" onClick={toggleFullscreen} title="Fullscreen">{fullscreen ? "⤡" : "⤢"}</button>
        </div>
      </header>

      {showToc && (
        <div className="reader-toc" onClick={() => setShowToc(false)}>
          <div className="reader-toc-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Contents</h3>
            <ul>
              {(layout?.chapterViews ?? []).map((c, i) => (
                <li key={i}>
                  <button onClick={() => { setView(c.view); setShowToc(false); }}>{c.title}</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!opened && book.coverImage ? (
        <div className="reader-cover-screen">
          <button className="reader-cover" onClick={() => setOpened(true)} title="Open the book">
            <img src={book.coverImage} alt={`${book.title} cover`} />
            <span className="reader-cover-hint">Open book</span>
          </button>
        </div>
      ) : (
        <div className="reader-stage" style={{ fontSize: `${fontScale}em` }}>
          <div className="reader-viewport" ref={viewportRef} style={layout ? { width: layout.viewportW, height: layout.pageH } : undefined}>
            {/* The translate lives on this plain wrapper; a fragmented multi-column
                container (the flow) silently ignores `transform`, so it must not
                carry the page-turn itself. */}
            <div className="reader-track" style={{ transform: `translateX(${-offset}px)` }}>
              <div className="reader-flow" ref={flowRef}>
                {book.chapters.map((ch, i) => (
                  <section className="reader-chapter" key={i} data-title={ch.title}>
                    {ch.partTitle && <h1 className="reader-part">{ch.partTitle}</h1>}
                    <h2 className="reader-chapter-title">{ch.title}</h2>
                    <div className="reader-prose" dangerouslySetInnerHTML={{ __html: ch.html }} />
                  </section>
                ))}
              </div>
            </div>
            {layout?.twoPage && <div className="reader-spine" />}
          </div>

          {/* Click zones for page turning. */}
          <button className="reader-zone reader-zone-left" onClick={prev} aria-label="Previous page" disabled={view <= 0} />
          <button className="reader-zone reader-zone-right" onClick={next} aria-label="Next page" disabled={view >= totalViews - 1} />

          <div className="reader-folio reader-folio-left">{firstFolio <= (layout?.totalPages ?? 1) ? firstFolio : ""}</div>
          {layout?.twoPage && (
            <div className="reader-folio reader-folio-right">
              {firstFolio + 1 <= (layout?.totalPages ?? 1) ? firstFolio + 1 : ""}
            </div>
          )}
          <div className="reader-progress">{view + 1} / {totalViews}</div>
        </div>
      )}
    </div>
  );
}
